import { Button } from '@components/Button';
import Block from '@components/Layout/Block';
import ConditionallyWrapWithUrl from '@components/MiscComponents/ConditionallyWrapWithUrl';
import SocialSubscribing, {
  SocialSubscribingProps,
} from '@components/SocialMediaLinks/SocialSubscribing';
import { Box } from '@mui/material';
import { searchFilterData } from '@util/api/listingAdvisors';
import { designationsData } from '@util/api/listingAdvisorsDesignations';
import { fallbackLanguageDictionary, getCurrentLocale } from '@util/locale';
import { getPhoneFormattedText, getPhoneURI } from '@util/phoneNumber';
import { isValidHttpUrl } from '@util/validateUrl';
import { Email, ExternalLink, Phone } from 'icons';
import { useTranslations } from 'next-intl';
import { FC } from 'react';
import {
  AdvisorSocials,
  BioWrapper,
  Eyebrow,
  ImageBox,
  ImageWrapper,
  InfoBlock,
  InfoBlockContact,
  InfoBlockLicense,
  Label,
  MainInfoWrapper,
  Name,
  Title,
  Wrapper,
} from './styles';

export interface AdvisorBioHeroProps {
  //image?: ResolvedImageData;
  image?: any;
  name?: string;
  title: string;
  supplementalTitle?: string;
  pronouns?: string;
  specializations?: string[];
  designations?: string[];
  license?: any[];
  phone?: string;
  languages?: string[];
  email?: string;
  website?: string;
  url: string;
  city?: string;
  state?: string;
  postalCode?: string;
  office?: string;
  address2?: string;
  socialProfiles?: {
    url: string;
    type: string;
  }[];
  imageAspectRatio?: number | number[];
  associateShopName?: string;
  associateShopHomePage: string;
  isTeamBio?: boolean;
}

//Social Media Icons and links
const MappedSocialSubscribing = ({ socialMediaArr }) => {
  const props: SocialSubscribingProps = {
    socialMedias: socialMediaArr,
  };
  return <SocialSubscribing {...props} />;
};

export const AdvisorBioHero: FC<AdvisorBioHeroProps> = ({
  image,
  name,
  title,
  supplementalTitle,
  pronouns,
  specializations,
  designations,
  license,
  phone,
  languages,
  email,
  website,
  url,
  city,
  state,
  postalCode,
  office,
  address2,
  socialProfiles,
  imageAspectRatio,
  associateShopName,
  associateShopHomePage,
  isTeamBio,
}) => {
  const t = useTranslations();
  const locale = getCurrentLocale();

  // Social Media
  //create an array of social media objects according to SocialMedia interface
  const socialMediaArr = socialProfiles?.map((social) => {
    //check if platform is provided, then map to the correct platform
    if (social == null) return;
    if (social.type) {
      return {
        platform: social.type,
        url: social.url,
      };
    }
    //it if no platform is listed, then manually check the urls provided and see if they are facebook, twitter, linkedin, tiktok, youtube or instagram
    else if (social.url.includes('facebook')) {
      return {
        platform: 'facebook',
        url: social.url,
      };
    } else if (social.url.includes('twitter')) {
      return {
        platform: 'twitter',
        url: social.url,
      };
    } else if (social.url.includes('linkedin')) {
      return {
        platform: 'linkedin',
        url: social.url,
      };
    } else if (social.url.includes('instagram')) {
      return {
        platform: 'instagram',
        url: social.url,
      };
    } else if (social.url.includes('youtube')) {
      return {
        platform: 'youtube',
        url: social.url,
      };
    } else if (social.url.includes('tiktok')) {
      return {
        platform: 'tiktok',
        url: social.url,
      };
    } else if (social.url.includes('whatsapp')) {
      return {
        platform: 'whatsapp',
        url: social.url,
      };
    } else if (social.url.includes('insider')) {
      return {
        platform: 'insider',
        url: social.url,
      };
    }
  });

  // Languages
  let languageNameExtender = (arr: string[]) => {
    let langArr: string[] = [];
    arr.forEach((code) => {
      const langMap = { sp: 'es' };
      let intlLang = new Intl.DisplayNames([locale.replace(/-.*/, '')], {
        type: 'language',
      });
      // If the code is present within our custom fallback dictionary, preferentially display that
      langArr.push(
        (fallbackLanguageDictionary[code] && fallbackLanguageDictionary[code][locale]) ||
          intlLang.of(langMap[code] || code) ||
          code,
      );
    });
    return langArr.join(', ');
  };

  // Specializations
  // match each specialization value with correct title from searchFilterData
  let specializationTitleString;
  if (specializations && specializations.length) {
    let specializationTitleArr: string[] = [];
    specializations.forEach((spec) => {
      searchFilterData.specializations.forEach((obj) => {
        if (obj.value === spec) {
          specializationTitleArr.push(obj.title);
        }
      });
    });
    specializationTitleString = specializationTitleArr.join(', ');
  }

  // Designations
  // match each designation value with correct title from designationsData
  let designationTitleString;
  if (designations && designations.length) {
    let designationTitleArr: string[] = [];
    designations.forEach((spec) => {
      designationsData.forEach((obj) => {
        if (obj.value === spec) {
          designationTitleArr.push(obj.title);
        }
      });
    });
    designationTitleString = designationTitleArr.join(', ');
  }

  const translatedTitle = t((title || supplementalTitle || '') as any);
  const formattedCity = city ? `${city}, ` : '';
  const formattedState = state ? `${state} ` : '';
  const formattedPostalCode = postalCode ? postalCode : '';
  const formattedWebsiteUrl = !url.includes('https') ? `https://${url}` : url;

  return (
    <Block>
      <Wrapper>
        <BioWrapper>
          <MainInfoWrapper>
            {name && <Name data-testid="advisor-name">{name}</Name>}
            {translatedTitle && (
              <Title
                dangerouslySetInnerHTML={{
                  __html: translatedTitle.replace('•', '<span>•</span>'),
                }}
                data-testid="advisor-title"
              />
            )}
            {pronouns && pronouns !== 'none' && (
              <InfoBlock>
                <span className="info-block__pronouns">{pronouns}</span>
              </InfoBlock>
            )}
          </MainInfoWrapper>
          {languages && languages.length ? (
            <InfoBlock>
              <Label data-testid="advisor-language-label">{t('Languages Spoken')}:</Label>{' '}
              <span data-testid="language-values">{languageNameExtender(languages)}</span>
            </InfoBlock>
          ) : null}
          {specializationTitleString && (
            <InfoBlock>
              <Label data-testid="advisor-specializations">{t('Specializations')}:</Label>{' '}
              {specializationTitleString}
            </InfoBlock>
          )}
          {designationTitleString && (
            <InfoBlock>
              <Label data-testid="advisor-designations">{t('Designations')}:</Label>{' '}
              {designationTitleString}
            </InfoBlock>
          )}
          {license &&
            license.map((obj, ind) => {
              if (ind + 1 === license.length) {
                return (
                  <InfoBlockLicense key={`li-${ind}`} data-testid="advisor-license">
                    {obj.licenseState ||
                      (obj.state && <Label>{`${obj.licenseState || obj.state}: `}</Label>)}
                    {obj.licenseNumber || ''}
                  </InfoBlockLicense>
                );
              } else {
                return (
                  <InfoBlockLicense key={`li-${ind}`}>
                    {obj.licenseState ||
                      (obj.state && <Label>{`${obj.licenseState || obj.state}: `}</Label>)}
                    {`${obj.licenseNumber || ''}, `}
                  </InfoBlockLicense>
                );
              }
            })}
          {associateShopName && (
            <InfoBlock style={{ lineHeight: '1.5rem' }} data-testid="advisor-office-container">
              <Label data-testid="advisor-shop-label">{t('Shop')}: </Label>
              <ConditionallyWrapWithUrl
                dataTestId="advisor-office-name"
                // TEMP check for team bio until shop urls come through
                href={isValidHttpUrl(associateShopHomePage)}
              >
                {associateShopName}
              </ConditionallyWrapWithUrl>
              {office && <Box data-testid="advisor-office-address">{office}</Box>}
              {address2 && <Box data-testid="advisor-office-address2">{address2}</Box>}
              <Box data-testid="advisor-office-cityzipcode">{`${formattedCity}${formattedState}${formattedPostalCode}`}</Box>
            </InfoBlock>
          )}
          {phone || email || website ? (
            <Eyebrow data-testid="advisor-contactme">{t('Contact Me')}</Eyebrow>
          ) : null}

          {phone && (
            <InfoBlockContact data-testid="advisor-phone">
              <a href={getPhoneURI(phone)}>
                <Phone />
                {getPhoneFormattedText(phone)}
              </a>
            </InfoBlockContact>
          )}
          {email && (
            <InfoBlockContact data-testid="advisor-email">
              <a href={`mailto:${email}`}>
                <Email />
                {email}
              </a>
            </InfoBlockContact>
          )}
          {socialMediaArr?.length ? (
            <>
              <Eyebrow data-testid="advisor-followme">{t('Follow Me')}</Eyebrow>
              <AdvisorSocials data-testid="advisor-socials-container">
                <MappedSocialSubscribing socialMediaArr={socialMediaArr} />
              </AdvisorSocials>
            </>
          ) : null}
          {website && (
            <Button
              dataTestId="advisor-website-personal"
              url={formattedWebsiteUrl}
              variant="primaryRed"
              target
              arrow={false}
              label={isTeamBio ? t('Visit Our Website') : t('Visit My Website')}
              icon="ExternalLink"
              iconPosition="left"
              style={{ marginTop: '36px' }}
            />
          )}
        </BioWrapper>
        <ImageWrapper>
          <ImageBox>
            <img src={image} />
          </ImageBox>
        </ImageWrapper>
      </Wrapper>
    </Block>
  );
};

export default AdvisorBioHero;
